<template>
    <section class="complex-component-requirement-item">
        <button
            type="button"
            class="complex-component-requirement-item__header"
            :aria-expanded="expanded ? 'true' : 'false'"
            :aria-controls="collapseId"
            @click="$emit('toggle')"
        >
            <span class="complex-component-requirement-item__identity">
                <strong>{{ entry.title }}</strong>
                <small
                    v-for="path in entry.componentPaths"
                    :key="path"
                    class="text-less-muted"
                >
                    {{ path }}
                </small>
            </span>
            <span class="complex-component-requirement-item__summary">
                <WorkflowSaveStatus
                    :step="localSaveStep"
                    :show-text="false"
                    class="complex-component-requirement-item__save-status"
                />
                <span
                    :id="statusExplanationId"
                    :class="hasBlockingReasons
                        ? 'complex-component-requirement-item__status-with-explanation'
                        : null"
                >
                    <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
                    <feather-icon
                        v-if="hasBlockingReasons"
                        icon="InfoIcon"
                        size="14"
                    />
                </span>
                <b-popover
                    :target="statusExplanationId"
                    :disabled="!hasBlockingReasons"
                    triggers="hover"
                    placement="bottom"
                >
                    <template #title>
                        {{ statusExplanationTitle }}
                    </template>
                    <ul class="complex-component-requirement-item__reasons mb-0 pl-1">
                        <li
                            v-for="reason in blockingReasons"
                            :key="reason.id"
                        >
                            {{ reasonMessage(reason) }}
                        </li>
                    </ul>
                </b-popover>
                <span class="text-less-muted">{{ substanceSummary }}</span>
                <feather-icon :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
            </span>
        </button>

        <b-collapse
            :id="collapseId"
            :visible="expanded"
        >
            <div class="complex-component-requirement-item__body">
                <div
                    v-for="question in entry.questions"
                    :key="question.id"
                    class="complex-component-requirement-item__question"
                >
                    <div>
                        <div
                            v-if="entry.questions.length > 1"
                            class="font-weight-bolder"
                        >
                            {{ question.list.title }}
                        </div>
                        <div
                            v-if="question.pathLabels.length"
                            class="text-less-muted font-small-2"
                        >
                            {{ question.pathLabels.join(', ') }}
                        </div>
                        <div>{{ question.list.question }}</div>
                    </div>
                    <div class="complex-component-requirement-item__answers">
                        <b-form-radio
                            :name="`complex-requirement-${requirement.id}-${entry.key}-${question.id}`"
                            :checked="question.state"
                            :value="true"
                            @change="answerQuestion(question, true)"
                        >
                            {{ $t('Component(Choice):yes') }}
                        </b-form-radio>
                        <b-form-radio
                            :name="`complex-requirement-${requirement.id}-${entry.key}-${question.id}`"
                            :checked="question.state"
                            :value="false"
                            @change="answerQuestion(question, false)"
                        >
                            {{ $t('Component(Choice):no') }}
                        </b-form-radio>
                    </div>
                </div>

                <div
                    v-if="entry.scipVisible"
                    class="complex-component-requirement-item__scip"
                >
                    <b-form-group class="mb-0">
                        <template #label>
                            {{ translate('Component(Label):scip-number', 'SCIP number') }}
                            <span
                                v-if="showScipRequiredMarker"
                                class="text-danger ml-50"
                            >
                                {{ translate('Component(Text):required', 'Required') }}
                            </span>
                        </template>
                        <b-form-input
                            :value="entry.scipNumber"
                            :state="entry.scipRequired && entry.scipNumber.trim().length === 0
                                ? false
                                : null"
                            maxlength="255"
                            @input="changeScipNumber"
                        />
                    </b-form-group>
                </div>

                <template v-if="canSelectSubstances">
                    <div
                        v-if="entry.placements.length"
                        class="complex-component-requirement-item__substances"
                    >
                        <SubstanceRequirementSummary
                            v-for="placement in entry.placements"
                            :key="placementKey(placement)"
                            :placement="placement"
                            :list="placement.list || requirement.list"
                            :property-config="propertyConfig"
                            :hide-candidate-list-categories="entry.hideCandidateListCategories"
                            :editor-expanded="activeEditorKey === placementKey(placement)"
                            :information-required="placement.compliance
                                && placement.compliance.status !== 'complete'"
                            :show-requirement-status="showSubstanceRequirementStatus"
                            @toggle-editor="toggleEditor(placement)"
                            @change="(substance, properties, callbacks) => changeProperties(
                                placement,
                                substance,
                                properties,
                                callbacks,
                            )"
                            @exclude="(substance, callbacks) => excludeSubstance(
                                placement,
                                substance,
                                callbacks,
                            )"
                        />
                    </div>
                    <div
                        v-else
                        class="text-muted mt-1"
                    >
                        {{ translate(
                            'Page(ReceivedRequest):Component(ComplianceInformation):no-substances-selected',
                            'No substances selected for this requirement.',
                        ) }}
                    </div>

                    <b-button
                        class="mt-1"
                        variant="outline-primary"
                        @click="selectSubstances"
                    >
                        <feather-icon
                            icon="PlusIcon"
                            class="mr-50"
                        />
                        {{ translate(
                            'Page(ReceivedRequest):Component(ComplianceInformation):add-substance',
                            'Add substance',
                        ) }}
                    </b-button>

                    <CandidateListSafetyInstructions
                        v-if="showSafetyInstructions"
                        :instructions="entry.safetyInstructions"
                        @action="handleSafetyInstructionAction"
                    />
                </template>

                <div
                    v-if="allQuestionsNegative"
                    class="text-success mt-1"
                >
                    {{ translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):no-relevant-substances',
                        'No relevant substances declared.',
                    ) }}
                </div>

            </div>
        </b-collapse>
    </section>
</template>

<script>
import {
    BBadge,
    BButton,
    BCollapse,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BPopover,
} from 'bootstrap-vue'
import SubstanceRequirementSummary from './SubstanceRequirementSummary.vue'
import CandidateListSafetyInstructions from './CandidateListSafetyInstructions.vue'
import WorkflowSaveStatus from '@/views/request/received/workflow/components/WorkflowSaveStatus.vue'
import { blockingReasons } from '../requirement-checks'

export default {
    name: 'ComplexComponentRequirementItem',
    components: {
        BBadge,
        BButton,
        BCollapse,
        BFormGroup,
        BFormInput,
        BFormRadio,
        BPopover,
        CandidateListSafetyInstructions,
        SubstanceRequirementSummary,
        WorkflowSaveStatus,
    },
    props: {
        entry: { type: Object, required: true },
        requirement: { type: Object, required: true },
        propertyConfig: { type: Object, required: true },
        expanded: { type: Boolean, default: false },
        showSubstanceRequirementStatus: { type: Boolean, default: true },
    },
    data() {
        return {
            activeEditorKey: null,
            knownPlacementKeys: [],
            placementsInitialized: false,
            pendingSaveOperations: 0,
            activeSaveOperations: 0,
            localSaveError: null,
        }
    },
    computed: {
        showSafetyInstructions() {
            return this.entry.safetyInstructions
                && (this.entry.facts || []).some(fact => (
                    fact.id === 'candidate-list-substances-above-covered-weight'
                    && Number(fact.value) > 0
                ))
        },
        showScipRequiredMarker() {
            return this.entry.scipRequired
                && String(this.entry.scipNumber || '').trim().length === 0
        },
        collapseId() {
            return `complex-component-requirement-${this.requirement.id}-${this.entry.key}`
        },
        statusVariant() {
            return this.entry.status === 'complete' ? 'light-success' : 'light-warning'
        },
        statusLabel() {
            return this.entry.status === 'complete'
                ? this.translate('Component(Status):complete', 'Complete')
                : this.translate('Component(Status):information-required', 'Information required')
        },
        blockingReasons() {
            return blockingReasons(this.entry.checks)
        },
        blockingReasonSignature() {
            return this.blockingReasons.map(reason => reason.id).join('|')
        },
        hasBlockingReasons() {
            return this.blockingReasons.length > 0
        },
        statusExplanationId() {
            return `complex-component-status-${this.requirement.id}-${this.entry.key}`
        },
        statusExplanationTitle() {
            return this.translate(
                'Page(ReceivedRequest):Component(ComplianceInformation):status-explanation-title',
                'Why is information required?',
            )
        },
        substanceSummary() {
            const count = this.entry.placements.length
            return count === 1 ? '1 substance' : `${count} substances`
        },
        canSelectSubstances() {
            return this.entry.questions.length === 0 || this.entry.selectorListIds.length > 0
        },
        allQuestionsNegative() {
            return this.entry.questions.length > 0
                && this.entry.questions.every(question => question.state === false)
        },
        localSaveStep() {
            const hasActivity = this.pendingSaveOperations > 0 || this.activeSaveOperations > 0
            return {
                id: `complex-component-${this.entry.key}`,
                isSaving: this.activeSaveOperations > 0,
                isDirty: this.pendingSaveOperations > 0,
                error: hasActivity ? null : this.localSaveError,
            }
        },
    },
    watch: {
        'entry.placements': {
            immediate: true,
            handler(placements) {
                const keys = placements.map(this.placementKey)
                const firstNewKey = keys.find(key => !this.knownPlacementKeys.includes(key))
                const shouldOpenEditor = this.entry.status !== 'complete'
                    && this.entry.missingProperties.length > 0
                    && !this.activeEditorKey

                if (this.placementsInitialized && firstNewKey) {
                    this.activeEditorKey = firstNewKey
                } else if (!this.placementsInitialized && shouldOpenEditor) {
                    this.activeEditorKey = keys[0] || null
                }

                this.knownPlacementKeys = keys
                this.placementsInitialized = true
            },
        },
        blockingReasonSignature() {
            this.hideStatusExplanation()
        },
    },
    beforeDestroy() {
        this.hideStatusExplanation()
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        reasonMessage(reason) {
            return this.translate(reason.messageKey, reason.fallbackMessage)
        },
        hideStatusExplanation() {
            this.$root.$emit('bv::hide::popover', this.statusExplanationId)
        },
        placementKey(placement) {
            const substance = placement.container.substance || {}
            return `${this.entry.key}-${substance.id}-${(placement.list || this.requirement.list).id}`
        },
        beginSaveOperation() {
            this.pendingSaveOperations += 1
            this.localSaveError = null
            let settled = false
            let saving = false

            const settle = error => {
                if (settled || this._isDestroyed) {
                    return
                }
                settled = true
                if (saving) {
                    this.activeSaveOperations = Math.max(0, this.activeSaveOperations - 1)
                } else {
                    this.pendingSaveOperations = Math.max(0, this.pendingSaveOperations - 1)
                }
                if (error) {
                    this.localSaveError = error
                }
            }

            return {
                savingCallback: () => {
                    if (settled || saving || this._isDestroyed) {
                        return
                    }
                    saving = true
                    this.pendingSaveOperations = Math.max(0, this.pendingSaveOperations - 1)
                    this.activeSaveOperations += 1
                },
                completionCallback: () => settle(null),
                failureCallback: error => settle(error),
            }
        },
        combineSaveCallbacks(...callbacks) {
            const invoke = (name, value) => callbacks.forEach(callback => {
                if (callback && typeof callback[name] === 'function') {
                    callback[name](value)
                }
            })
            return {
                savingCallback: () => invoke('savingCallback'),
                completionCallback: () => invoke('completionCallback'),
                failureCallback: error => invoke('failureCallback', error),
            }
        },
        selectSubstances() {
            this.$emit('select', {
                entry: this.entry,
                createSaveCallbacks: () => this.beginSaveOperation(),
            })
        },
        toggleEditor(placement) {
            const key = this.placementKey(placement)
            this.activeEditorKey = this.activeEditorKey === key ? null : key
        },
        changeProperties(placement, substance, properties, callbacks = {}) {
            const saveCallbacks = this.combineSaveCallbacks(
                callbacks,
                this.beginSaveOperation(),
            )
            this.$emit('action', {
                type: 'component-substance-properties.change',
                payload: {
                    componentId: this.entry.product.id,
                    container: placement.container,
                    substance,
                    properties,
                    ...saveCallbacks,
                },
            })
        },
        excludeSubstance(placement, substance, callbacks = {}) {
            if (this.activeEditorKey === this.placementKey(placement)) {
                this.activeEditorKey = null
            }
            const saveCallbacks = this.combineSaveCallbacks(
                callbacks,
                this.beginSaveOperation(),
            )
            this.$emit('action', {
                type: 'component-substance.exclude',
                payload: {
                    componentId: this.entry.product.id,
                    substance,
                    save: true,
                    ...saveCallbacks,
                },
            })
        },
        changeScipNumber(value) {
            this.$emit('action', {
                type: 'component-scip-number.change',
                payload: {
                    componentId: this.entry.product.id,
                    value,
                    ...this.beginSaveOperation(),
                },
            })
        },
        handleSafetyInstructionAction(action) {
            const types = {
                'safe-use-applicability.change': 'component-safe-use-applicability.change',
                'safe-use-instructions.change': 'component-safe-use-instructions.change',
                'disassembling-instructions.change': 'component-disassembling-instructions.change',
            }
            this.$emit('action', {
                ...action,
                type: types[action.type],
                payload: {
                    ...action.payload,
                    componentId: this.entry.product.id,
                },
            })
        },
        async answerQuestion(question, state) {
            const affectedPlacements = this.entry.placements.filter(placement => (
                question.branchListIds.some(listId => (
                    !Array.isArray(placement.container.lists)
                    || placement.container.lists.some(list => String(list.id) === String(listId))
                ))
            ))
            if (state === false && !await this.confirmSubstanceRemoval(affectedPlacements)) {
                this.$nextTick(() => this.$forceUpdate())
                return
            }

            this.$emit('action', {
                type: 'component-list-question.update',
                payload: {
                    componentId: this.entry.product.id,
                    list: question.list,
                    state,
                    substances: state === false
                        ? affectedPlacements.map(placement => placement.container.substance)
                        : [],
                    ...this.beginSaveOperation(),
                },
            })
        },
        async confirmSubstanceRemoval(placements) {
            if (
                placements.length === 0
                || !this.$bvModal
                || typeof this.$bvModal.msgBoxConfirm !== 'function'
            ) {
                return true
            }

            return this.$bvModal.msgBoxConfirm(
                this.translate(
                    'Page(ReceivedRequest):Component(ComplianceInformation):remove-substances-confirmation',
                    'The substances selected for this requirement will be removed. Continue?',
                ),
                {
                    title: this.translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):change-answer-confirmation-title',
                        'Change answer?',
                    ),
                    okTitle: this.translate('Component(Button):continue', 'Continue'),
                    cancelTitle: this.translate('Component(Button):cancel', 'Cancel'),
                    okVariant: 'danger',
                    cancelVariant: 'outline-secondary',
                    centered: true,
                },
            )
        },
    },
}
</script>

<style lang="scss" scoped>
.complex-component-requirement-item {
    border: 1px solid #ebe9f1;
    border-radius: .357rem;

    & + & {
        margin-top: .75rem;
    }

    &__header {
        align-items: center;
        background: transparent;
        border: 0;
        color: inherit;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
        padding: 1rem;
        text-align: left;
        width: 100%;
    }

    &__identity {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    &__summary,
    &__answers {
        align-items: center;
        display: flex;
        flex: 0 0 auto;
        gap: 1rem;
    }

    &__save-status {
        margin-left: 0 !important;
    }

    &__status-with-explanation {
        align-items: center;
        color: #6e6b7b;
        cursor: help;
        display: inline-flex;
        gap: .35rem;
    }

    &__reasons {
        min-width: 15rem;

        li + li {
            margin-top: .35rem;
        }
    }

    &__body {
        border-top: 1px solid #ebe9f1;
        padding: 1rem;
    }

    &__scip {
        margin-top: 1rem;
        max-width: 34rem;
        padding-top: 1rem;
    }

    &__question {
        align-items: flex-start;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

    &__question + &__question {
        border-top: 1px solid #ebe9f1;
        margin-top: .75rem;
        padding-top: .75rem;
    }

    &__substances {
        display: grid;
        gap: .75rem;
        margin-top: 1rem;
    }
}

@media (max-width: 767.98px) {
    .complex-component-requirement-item {
        &__header,
        &__question {
            align-items: stretch;
            flex-direction: column;
        }

        &__summary {
            justify-content: space-between;
        }
    }
}
</style>
